import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, shareReplay } from 'rxjs';
import { SessionStorageService } from './session-storage.service';
import { IAppointmentResponse } from '../interfaces/appointmentsResponse';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBarComponent } from '../mat-snack-bar/mat-snack-bar.component';
import { Iinvoice } from '../interfaces/invoice';
import {
  AppointmentDetails, AppointmentRequest, Client, DepartmentDto,
  LoginResponse, MakeDto, PaymentMethodsResponse, Resource, Service,
  Timeslot, TimeslotRequest
} from './client.service';

import * as langConfig from "../../assets/languageConfig/languageConfig.json";

@Injectable({
  providedIn: 'root'
})
export class AppService {
  companyId!: string;
  locationId!: string;
  languageConfiguration: any = langConfig;
  _loader = new BehaviorSubject(false);
  loader$ = this._loader.asObservable();
  _componentReady = new BehaviorSubject(false);
  componentReady$ = this._componentReady.asObservable();

  appointmentResponse$!: Observable<IAppointmentResponse>;
  invoices$!: Observable<Iinvoice[]>;
  invoicesById$!: Observable<string>;
  private makes$?: Observable<MakeDto[]>;
  private departments$?: Observable<DepartmentDto[]>;
  sessionExpiredMessage: string = $localize`Your session expired due to inactivity or a network issue, please relaunch the page.`;
  errorMessage: string = $localize`Online Appointment Booking not supported. Please contact dealer directly.`;
  confirmationErrorMessage: string = $localize`Appointment could not be created. Please contact dealer directly.`;

  constructor(
    private http: HttpClient,
    private sessionStorageService: SessionStorageService,
    private _snackBar: MatSnackBar,
    public dialog: MatDialog,
    public client: Client
  ) { }

  setLoading(loading: boolean) {
    this._loader.next(loading);
  }

  setComponentReady(ready: boolean) {
    this._componentReady.next(ready);
  }

  handleErrorMessage(error: any) {
    let message = 'Something went wrong';
    if (
      error?.error?.detail?.includes("authorization") ||
      error?.detail?.includes("authorization")
    ) {
      message = this.sessionExpiredMessage;
      this.showSnackBar(message);
      return;
    }

     if (
      error?.error?.detail?.includes("Invalid location") ||
      error?.detail?.includes("Invalid location")
    ) {
      message = this.errorMessage;
      this.showSnackBar(message);
      return;
    }

     if (
      error?.error?.detail?.includes("License verification") ||
      error?.detail?.includes("License verification") ||  error?.error?.detail?.includes("Codeunit")||
      error?.detail?.includes("Codeunit")) {
      message = this.errorMessage;
      this.showSnackBar(message);
      return;
    }
    const errorDetail = error?.detail || error?.error?.detail;
    if (
      typeof errorDetail === 'string' &&
      (
        errorDetail.includes("PostAppointment") ||
        errorDetail.includes("RunTime Error on PostAppointment method") ||
        errorDetail.includes("You cannot send the email") ||
        errorDetail.includes("Outlook")
      )
    ) {
      this.showSnackBar(this.confirmationErrorMessage);
      return;
    }
    if (!error) {
      this.showSnackBar(message);
      return;
    }

    if (typeof error.error === 'string') {
      try {
        const errObj = JSON.parse(error.error);
        message = errObj.detail || errObj.Detail || errObj.message || message;
      } catch {
        message = error.detail || error.Detail || message;
      }
      this.showSnackBar(message);
      return;
    }

    else if (error.error && typeof error.error === 'object') {
      message = error.error.detail || error.error.Detail || error.error.message || message;
      this.showSnackBar(message);
      return;
    }

    else if (error.detail) {
      try {
        const detailStr = typeof error.detail === 'string'
          ? error.detail
          : JSON.stringify(error.detail);
        const detailMatch = detailStr.match(/"Detail"\s*:\s*"([^"]*)"/);

        if (detailMatch && detailMatch[1]) {
          message = detailMatch[1];
        } else {
          message = error.Detail || error.message || 'An error occurred';
        }
      } catch {
        message = error.Detail || error.message || 'An error occurred';
      }
      this.showSnackBar(message);
      return;
    }

    if (typeof error === 'string') {
      this.showSnackBar(error);
      return;
    }
  }

  private showSnackBar(message: string) {
    this._snackBar.openFromComponent(MatSnackBarComponent, {
      data: message,
      duration: 10000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['custom-snack-bar']
    });
  }

  loginWithToken(token: string): Observable<LoginResponse> {
    this.sessionStorageService.removeSessionItem('oab-profile');
    const lang = window.localStorage.getItem('lang') ?? 'en-US';
    return this.client.pageLoadDetails(token, lang).pipe(
      shareReplay(1)
    );
  }

  getDepartments(): Observable<DepartmentDto[]> {
    if (!this.departments$) {
      const [companyId] = this.getCompanyAndLocationId();
      const lang = this.getLanguage();
      this.departments$ = this.client.getDepartments(companyId, lang).pipe(
        shareReplay(1)
      );
    }
    return this.departments$;
  }

  getMakes(): Observable<MakeDto[]> {
    if (!this.makes$) {
      const [companyId] = this.getCompanyAndLocationId();
      const lang = this.getLanguage();
      this.makes$ = this.client.getMakes(companyId, lang).pipe(
        shareReplay(1)
      );
    }
    return this.makes$;
  }

  getServices(obj: any): Observable<Service[]> {
    const [companyId, locationId] = this.getCompanyAndLocationId();
    const lang = this.getLanguage();
    return this.client.getServices(companyId, locationId, lang, obj?.department, obj?.make);
  }

  getResources(obj: any): Observable<Resource[]> {
    const [companyId, locationId] = this.getCompanyAndLocationId();
    const lang = this.getLanguage();
    return this.client.getResources(companyId, locationId,obj.department,lang);
  }

  getTimeSlots(obj: TimeslotRequest): Observable<Timeslot[]> {
    const [companyId, locationId] = this.getCompanyAndLocationId();
    const lang = this.getLanguage();
    return this.client.getTimeSlots(companyId, locationId, lang, obj);
  }

  getPaymentMethods(): Observable<PaymentMethodsResponse[]> {
    const [companyId] = this.getCompanyAndLocationId();
    const lang = this.getLanguage();
    return this.client.getPaymentMethods(companyId, lang);
  }

  postAppointment(reqObject: AppointmentRequest): Observable<string> {
    const [companyId] = this.getCompanyAndLocationId();
    const lang = this.getLanguage();
    return this.client.createAppointment(companyId, lang, reqObject).pipe(shareReplay(1));
  }

  getAppointment(appointmentId: string): Observable<AppointmentDetails> {
    const [companyId] = this.getCompanyAndLocationId();
    const lang = this.getLanguage();
    return this.client.getAppointment(companyId, appointmentId, lang).pipe(shareReplay(1));
  }

  cancelAppointment(appointmentId: string): Observable<any> {
    const [companyId] = this.getCompanyAndLocationId();
    const lang = this.getLanguage();
    return this.client.CancelAppointment(companyId, appointmentId, lang).pipe(shareReplay(1));
  }

  getCompanyAndLocationId(): [string, string] {
    const companiesListStrong = this.sessionStorageService.getSessionItem('companiesList');
    const companiesList = companiesListStrong ? JSON.parse(companiesListStrong) : null;

    let companyId = '';
    let locationId = '';

    if (companiesList) {
      companiesList.find((element: any) => {
        if (element.isSelected) {
          companyId = element.id || element.Id;
          const locations = element.locations || element.Locations;
          if (locations) {
            locations.find((ele: any) => {
              if (ele.isSelected) {
                locationId = ele.id || ele.Id;
              }
            });
          }
        }
      });
    }

    if (!companyId && companiesList?.length) {
      companyId = companiesList[0].id || companiesList[0].Id;
    }

    return [companyId, locationId];
  }

  private getLanguage(): string {
    return this.sessionStorageService.getSessionItem('language') ?? 'en-US';
  }

  getCurrentFormattedDate(reqdate: string) {
    const date = new Date(reqdate);
    return date.toISOString();
  }

  convertCustomDateToNormalFormate(reqDate: string) {
    const [datePart, year, timePart] = reqDate.replace(/^[a-zA-Z]+, /, '').split(', ');
    const [day, monthStr] = datePart.split(' ');
    const monthMapping: { [key: string]: number } = {
      January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
      July: 6, August: 7, September: 8, October: 9, November: 10, December: 11
    };
    const month = monthMapping[monthStr];
    const dayString = day.replace(/st|nd|rd|th/, '');
    const timeRegex = /(\d{1,2}):(\d{2})\s(AM|PM)/;
    const [, hoursStr, minutes, period] = timePart.match(timeRegex)!;
    let hours = parseInt(hoursStr);
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${year}-${month + 1}-${parseInt(dayString)}T${hours.toString().padStart(2, '0')}:${minutes}:00`;
  }

  setDefaultLanguage() {
    if (typeof window !== 'undefined') {
      const currentLanguage = window.localStorage.getItem("lang") || navigator.language;
      window.localStorage.setItem("lang", currentLanguage);
    }
  }
}
